import { MelodyLib } from '@tunasong/music-lib'
import { buildClickTrack } from '../melody-capture/click-track.js'
import type { Stab } from '../player/stab-scheduler.js'

/**
 * The eight clicks the latency tap test runs against - pure.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.3
 */

/**
 * The calibration tempo. A CONSTANT §6.3 DID NOT GIVE.
 *
 * The tap test measures the device, not the music, so it runs at a fixed pulse rather than the
 * melody's: a calibration whose answer moved with the song's tempo would be measuring the player.
 * 100 bpm is the Pitch Trainer's count-in tempo - already this app's "walkable pulse", fast enough
 * not to drag and slow enough to land on - and eight clicks 600 ms apart is 4.8 s, short enough to
 * redo without irritation when a run comes back with too few taps.
 */
export const latencyClickBpm = 100

const beatMs = MelodyLib.beatMs(latencyClickBpm)

/**
 * Eight knocks, one pitch, one weight.
 *
 * `meter: { top: 1 }` makes every click a downbeat, so all eight carry the accent velocity and
 * none of them is "the one". An accent pattern would suggest a metre, and a tapper who starts
 * phrasing against a metre is no longer producing the reflex this test measures.
 *
 * `countIn: false` because there is nothing to count into: the run IS the clicks.
 */
export const latencyClickTrack = (): Stab[] =>
  buildClickTrack({
    bpm: latencyClickBpm,
    meter: { top: 1, bottom: 4 },
    bars: MelodyLib.latencyClickCount,
    countIn: false,
  })

/**
 * The click times `latencyFromTaps` measures against.
 *
 * Derived from the track the player actually hears rather than recomputed, so the two cannot
 * drift. A drift here would be baked into `inputLatencyMs` and then subtracted from every onset of
 * every take on that device.
 */
export const latencyClickTimesMs = (): number[] => latencyClickTrack().map(stab => stab.delayMs)

/**
 * How long the run listens - one beat past the last click, so a tap that lands late on the eighth
 * click is still collected rather than being the missed tap that fails the run.
 */
export const latencyRunDurationMs = (): number => MelodyLib.latencyClickCount * beatMs
